/**
 * FinalChecksStep Component
 *
 * Content-only component for displaying and managing final check items
 * during the pre-trading ritual. Shows a checklist of items the user
 * should verify before transitioning to live trading.
 *
 * This component renders just the content (no Card wrapper) - the parent
 * component (PreTradingPhase) is responsible for providing the Card chrome.
 *
 * This is different from FinalChecksSettingsModal (settings) which configures
 * WHAT items exist - this component lets users check off TODAY's items.
 */

import React, { useCallback, useMemo } from 'react';
import { Checkbox, EmptyState } from '@enfomentis/ui';
import { CheckSquare } from 'lucide-react';

// =============================================================================
// Types
// =============================================================================

/**
 * Final check item configuration from PreTradingConfigSnapshot.finalCheckItems
 */
export interface FinalCheckItemConfig {
  id: string;
  label: string;
}

/**
 * Final check entry for completion tracking (matches SessionDraft.finalChecks)
 */
export interface FinalCheckEntry {
  checkItemId: string;
  label: string;
  completed: boolean;
}

/**
 * Props for FinalChecksStep component
 */
export interface FinalChecksStepProps {
  /** Configured check items from PreTradingConfigSnapshot.finalCheckItems */
  finalCheckItems: FinalCheckItemConfig[];
  /** Current completion state (from SessionDraft.finalChecks) */
  finalChecks: FinalCheckEntry[];
  /** Callback when completion state changes */
  onChange: (finalChecks: FinalCheckEntry[]) => void;
}

// =============================================================================
// Component
// =============================================================================

/**
 * Final Checks Step Component
 *
 * Renders a checklist of final check items with completion tracking.
 * Shows empty state when no items configured.
 *
 * Note: This is a content-only component. Parent provides the Card wrapper.
 */
export const FinalChecksStep: React.FC<FinalChecksStepProps> = ({
  finalCheckItems,
  finalChecks,
  onChange,
}) => {
  // Build a map of checkItemId -> completed for quick lookup
  const completionMap = useMemo(() => {
    const map = new Map<string, boolean>();
    for (const entry of finalChecks) {
      map.set(entry.checkItemId, entry.completed);
    }
    return map;
  }, [finalChecks]);

  /**
   * Handle checkbox toggle for a specific item
   */
  const handleToggle = useCallback(
    (itemId: string, itemLabel: string, isCompleted: boolean) => {
      // Find existing entry for this item
      const existingIndex = finalChecks.findIndex((e) => e.checkItemId === itemId);

      let updatedChecks: FinalCheckEntry[];

      if (existingIndex >= 0) {
        // Update existing entry (also refresh label to match current config)
        updatedChecks = finalChecks.map((entry, index) =>
          index === existingIndex
            ? { ...entry, label: itemLabel, completed: isCompleted }
            : entry
        );
      } else {
        // Add new entry (item wasn't in finalChecks yet)
        updatedChecks = [
          ...finalChecks,
          {
            checkItemId: itemId,
            label: itemLabel,
            completed: isCompleted,
          },
        ];
      }

      onChange(updatedChecks);
    },
    [finalChecks, onChange]
  );

  // Empty state when no items configured
  if (finalCheckItems.length === 0) {
    return (
      <EmptyState
        icon={<CheckSquare size={24} />}
        title="No checklist items configured"
        description="Configure final check items in settings to track your pre-trading checklist"
      />
    );
  }

  return (
    <div className="space-y-3">
      {finalCheckItems.map((item) => {
        const isCompleted = completionMap.get(item.id) === true;

        return (
          <div key={item.id} className="w-full">
            <Checkbox
              isSelected={isCompleted}
              onValueChange={(checked) => handleToggle(item.id, item.label, checked)}
              classNames={{
                base: 'min-h-[44px] items-center',
                wrapper: 'mr-3',
              }}
            >
              <span className="text-base">{item.label}</span>
            </Checkbox>
          </div>
        );
      })}
    </div>
  );
};

export default FinalChecksStep;
